import { createTheme } from '@/shared/ui/mui';
declare module '@mui/material/styles' {
  interface PaletteColor {
    lighter: string;
    ultraLight: string;
    accent: string;
  }

  interface SimplePaletteColorOptions {
    lighter?: string;
    ultraLight?: string;
    accent?: string;
  }

  interface Palette {
    black: {
      main: string;
      light: string;
      lighter: string;
      contrastText: string;
    };
    red: {
      main: string;
      dark: string;
      light: string;
      lighter: string;
      ultraLight: string;
      accent: string;
      contrastText: string;
    };
    teal: {
      main: string;
      dark: string;
      light: string;
      lighter: string;
      contrastText: string;
    };
    purple: {
      main: string;
      light: string;
      lighter: string;
      contrastText: string;
    };
    amber: {
      main: string;
      light: string;
      lighter: string;
      contrastText: string;
    };
    green: {
      main: string;
      light: string;
      lighter: string;
      contrastText: string;
    };
  }

  interface PaletteOptions {
    black?: {
      main: string;
      light: string;
      lighter: string;
      contrastText: string;
    };
    red?: {
      main: string;
      dark: string;
      light: string;
      lighter: string;
      ultraLight?: string;
      accent?: string;
      contrastText: string;
    };
    teal?: {
      main: string;
      dark: string;
      light: string;
      lighter?: string;
      contrastText: string;
    };
    purple?: {
      main: string;
      light: string;
      lighter: string;
      contrastText: string;
    };
    amber?: {
      main: string;
      light: string;
      lighter: string;
      contrastText: string;
    };
    green?: {
      main: string;
      light: string;
      lighter: string;
      contrastText: string;
    };
  }
}

// Define color constants — exported so data/constants files can reference them
export const colors = {
  blue: {
    main: '#2563EB',
    dark: '#1D4ED8',
    light: '#DBEAFE',
    lighter: '#BFDBFE',
    ultraLight: '#EFF6FF',
    accent: '#60A5FA',
    contrastText: '#FFFFFF',
  },
  teal: {
    main: '#0891B2',
    dark: '#0E7490',
    light: '#ECFEFF',     // soft surface
    lighter: '#A5F3FC',   // border tint
    contrastText: '#000000',
  },
  purple: {
    main: '#7C3AED',
    light: '#F5F1FF',     // soft surface
    lighter: '#DDD6FE',   // border tint
    contrastText: '#FFFFFF',
  },
  amber: {
    main: '#B45309',
    light: '#FFFBEB',     // soft surface
    lighter: '#FDE68A',   // border tint
    contrastText: '#FFFFFF',
  },
  green: {
    main: '#15803D',
    light: '#ECFDF5',     // soft surface
    lighter: '#BBF7D0',   // border tint
    contrastText: '#FFFFFF',
  },
  grey: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
  },
  black: {
    main: '#0F172A',
    light: '#1E293B',
    lighter: '#334155',
    contrastText: '#FFFFFF',
  },
  red: {
    main: '#DC2626',
    dark: '#B91C1C',
    light: '#FEE2E2',
    lighter: '#FEF2F2',
    ultraLight: '#FFF5F5',
    accent: '#F87171',
    contrastText: '#FFFFFF',
  },
  status: {
    warning: {
      main: '#F59E0B',
      dark: '#C2410C',
      light: '#FEF3C7',
    },
    success: {
      main: '#166534',
      light: '#DCFCE7',
    },
    info: {
      main: '#1D4ED8',
      light: '#DBEAFE',
    },
    neutral: {
      main: '#334155',
      light: '#F1F5F9',
    },
    error: {
      main: '#DC2626',
      light: '#FEE2E2',
    },
  },
};

const Theme = createTheme({
  palette: {
    primary: {
      main: colors.blue.main,
      dark: colors.blue.dark,
      light: colors.blue.light,
      lighter: colors.blue.lighter,
      ultraLight: colors.blue.ultraLight,
      accent: colors.blue.accent,
      contrastText: colors.blue.contrastText,
    },
    error: {
      main: colors.red.main,
      dark: colors.red.dark,
      light: colors.red.light,
      contrastText: colors.red.contrastText,
    },
    warning: {
      main: colors.status.warning.main,
      light: colors.status.warning.light,
      contrastText: colors.black.contrastText,
    },
    background: {
      default: colors.grey[50],
      paper: '#FFFFFF',
    },
    grey: colors.grey,
    success: colors.status.success,
    info: colors.status.info,
    // Now TypeScript will recognize these custom colors
    black: colors.black,
    red: colors.red,
    teal: colors.teal,
    purple: colors.purple,
    amber: colors.amber,
    green: colors.green,
  },
  components: {
    // Button customization
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: colors.blue.main,
          color: colors.blue.contrastText,
          '&:hover': {
            backgroundColor: colors.blue.dark,
          },
        },
        outlinedPrimary: {
          borderColor: colors.blue.lighter,
          color: colors.blue.main,
          '&:hover': {
            backgroundColor: colors.blue.light,
            borderColor: colors.blue.main,
          },
        },
        containedError: {
          backgroundColor: colors.red.main,
          color: colors.red.contrastText,
          '&:hover': {
            backgroundColor: colors.red.dark,
          },
        },
        outlinedError: {
          borderColor: colors.red.lighter,
          color: colors.red.main,
          '&:hover': {
            backgroundColor: colors.red.light,
            borderColor: colors.red.main,
          },
        },
      },
    },

    // Input customization
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: colors.grey[50],
          '&:hover': {
            backgroundColor: colors.grey[100],
          },
        },
        input: {
          color: colors.blue.main,
          fontWeight: 'bold',
        },
        underline: {
          '&:after': {
            borderBottomColor: colors.blue.main,
          },
          '&:hover:not(.Mui-disabled):before': {
            borderBottomColor: colors.blue.lighter,
          },
        },
      },
    },

    // Input label customization
    MuiInputLabel: {
      styleOverrides: {
        filled: {
          color: colors.grey[500],
          '&.Mui-focused': {
            color: colors.blue.main,
          },
          '&.Mui-error': {
            color: colors.red.main,
          },
        },
      },
    },

    // Chip customization
    MuiChip: {
      styleOverrides: {
        root: {
          '&.MuiChip-filledPrimary': {
            backgroundColor: colors.blue.light,
            color: colors.blue.main,
          },
          '&.MuiChip-filledError': {
            backgroundColor: colors.red.light,
            color: colors.red.main,
          },
        },
      },
    },

    // Paper customization
    MuiPaper: {
      styleOverrides: {
        root: {
          '&.MuiPaper-elevation0': {
            backgroundColor: colors.grey[50],
          },
        },
      },
    },

    // Icon Button customization
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: colors.blue.light,
          },
        },
      },
    },
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 600,
    },
    subtitle2: {
      fontWeight: 600,
    },
  },
});

export default Theme;
